/**
 * Reconcile the active-scan queue against the authoritative reports list.
 *
 * The Scan History page mirrors in-progress reports into the active-scan queue
 * (so they get polled and show spinners) and hides those rows from the normal
 * table. That mirror must be bidirectional:
 *
 *   1. Add recent in-progress reports not yet in the queue.
 *   2. Evict queue entries whose report row is now terminal (completed/failed/partial).
 *   3. Evict queue entries that no longer appear in the reports list at all —
 *      they are either completed and paged out, or orphaned localStorage ghosts.
 *      Without this, stale localStorage entries silently hide every matching
 *      report row from the table (activeScanIds.has(report.scanId) → filtered out)
 *      even though the scans are long done. This was the regression introduced
 *      by #223: the Reports page went blank because old active-scan IDs in
 *      localStorage were never evicted when the report fell outside the first
 *      page of results returned by useReports.
 *
 * Pure function so it can be unit tested without rendering the page.
 */

import type { ActiveScan } from '@/hooks/useActiveScans';
import { isScanConfirmedTerminal } from '@/hooks/useActiveScans';

export interface ReconcileReport {
  scanId: string;
  status: string;
  target: string;
  branch?: string;
  targetType: string;
  scannedAt: string;
}

export interface ReconcileResult {
  toAdd: ActiveScan[];
  toRemove: string[];
}

export function reconcileActiveScans(
  reports: ReconcileReport[],
  activeScanIds: Set<string>,
  nowMs: number,
  cutoffMs: number
): ReconcileResult {
  const toAdd: ActiveScan[] = [];
  const toRemove: string[] = [];
  const cutoff = nowMs - cutoffMs;

  // Build a set of all scan IDs present in the current reports page so we can
  // detect active-queue entries that have fallen out of the list entirely.
  const reportScanIds = new Set(reports.map((r) => r.scanId));

  for (const report of reports) {
    const isInProgress = report.status === 'queued' || report.status === 'running';

    if (!isInProgress) {
      // Terminal (completed/failed/partial): drop any stale queue entry.
      if (activeScanIds.has(report.scanId)) toRemove.push(report.scanId);
      continue;
    }

    // In-progress and recent enough not to be a zombie: ensure it's queued.
    // Skip re-adding if the getScan poller has already confirmed this scan as
    // terminal — the Prisma row just hasn't caught up yet. Without this guard,
    // the reconcile loop re-adds the scan on every nowMs tick while the reports
    // list still shows 'running', causing the poller to fire a repeated toast
    // and the spinner to never clear.
    const isRecent = new Date(report.scannedAt).getTime() > cutoff;
    if (isRecent && !activeScanIds.has(report.scanId) && !isScanConfirmedTerminal(report.scanId)) {
      toAdd.push({
        scanId: report.scanId,
        target: report.target,
        branch: report.branch,
        targetType: report.targetType,
        status: report.status as 'queued' | 'running',
        createdAt: report.scannedAt,
      });
    }
  }

  // Evict queue entries that are absent from the reports list entirely.
  // These are orphaned localStorage ghosts (completed scans that were stored
  // before the queue was properly cleaned up, or scans from a prior session
  // that completed while the app was closed). The getScan poller will also
  // catch any that are genuinely still in-flight and re-add them via addScan,
  // so this eviction is safe even for scans that haven't resolved yet but
  // simply aren't in the current page window.
  for (const id of activeScanIds) {
    if (!reportScanIds.has(id)) {
      toRemove.push(id);
    }
  }

  return { toAdd, toRemove };
}
